import { useEffect, useRef, useState } from 'react';
import { Input } from '@crisellevaldez/speculo-ui';

export interface CreatableSelectOption {
  value: string;
  label: string;
}

interface CreatableSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: CreatableSelectOption[];
  placeholder?: string;
  disabled?: boolean;
  isLoading?: boolean;
  error?: string;
  /** Max number of suggestions rendered. Defaults to 50 (datasets like
   *  country-state-city can have 1000+ cities per state). */
  maxSuggestions?: number;
}

/**
 * Editable Select / combobox — renders like speculo-ui Input with a
 * popover list of suggestions filtered by what the user types. User can
 * pick a suggestion OR commit any free-typed value (no validation against
 * the list). Use when the data set is open-ended (e.g. city names where
 * the dictionary may miss a real entry).
 */
export const CreatableSelect = ({
  value,
  onChange,
  options,
  placeholder,
  disabled = false,
  isLoading = false,
  error,
  maxSuggestions = 50,
}: CreatableSelectProps) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const filtered = (() => {
    const query = value.trim().toLowerCase();
    const matches = query
      ? options.filter((o) => o.label.toLowerCase().includes(query))
      : options;
    return matches.slice(0, maxSuggestions);
  })();

  // Close on click-outside.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    window.addEventListener('mousedown', onPointerDown);
    return () => window.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  return (
    <div ref={wrapperRef} className="relative">
      <Input
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          if (!open) setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        disabled={disabled || isLoading}
        error={error}
      />
      {open && filtered.length > 0 && !disabled && (
        <ul
          role="listbox"
          className="absolute z-50 mt-1 w-full max-h-60 overflow-y-auto rounded-md border border-foreground/30 bg-background shadow-md text-sm scrollbar-custom"
        >
          {filtered.map((opt) => {
            const isSelected = opt.value === value;
            return (
              <li
                key={opt.value}
                role="option"
                aria-selected={isSelected}
                onMouseDown={(e) => {
                  // mousedown (not click) so it fires before the input
                  // blurs and tears down the listbox.
                  e.preventDefault();
                  onChange(opt.value);
                  setOpen(false);
                }}
                className={`px-3 py-2 cursor-pointer hover:bg-accent ${
                  isSelected ? 'bg-accent/60 font-medium' : ''
                }`}
              >
                {opt.label}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
